/**
 * Setting up an ingredient.
 *
 * WHY THE PERISHABLE FIELDS ARE THE POINT OF THIS FORM
 * ----------------------------------------------------
 * A hundred and twenty-one ingredients are marked perishable with no shelf
 * life recorded. That means nothing can be suggested when they are delivered,
 * and anything taken in without somebody typing a date can never appear on
 * the expiring report — so the report reads as "nothing is expiring" when it
 * means "nobody has ever entered a date". This form is where that is fixed,
 * and until it existed it could not be fixed at all.
 *
 * WHY THE REORDER LEVEL SITS BESIDE IT
 * ------------------------------------
 * It is the same question from the other end. The reorder level is what
 * decides whether an ingredient appears on the list of what is worth
 * ordering, and the ceiling is how much of it to buy back.
 */
import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';

import { api } from '../../lib/api.js';
import { Button } from '../../components/ui/Button.jsx';

const STORAGE = {
  dry: 'Dry store',
  cold: 'Cold room',
  frozen: 'Freezer',
  ambient: 'Ambient',
};

export function IngredientForm({ initial = {}, onClose, onSaved }) {
  const isNew = !initial.id;

  const [name, setName] = useState(initial.name ?? '');
  const [category, setCategory] = useState(initial.category ?? '');
  const [unitId, setUnitId] = useState(initial.unit_id ?? '');
  const [reorderLevel, setReorderLevel] = useState(String(initial.reorder_level ?? ''));
  const [maxStock, setMaxStock] = useState(
    initial.max_stock_level != null ? String(initial.max_stock_level) : ''
  );
  const [perishable, setPerishable] = useState(initial.is_perishable === 1);
  const [shelfLife, setShelfLife] = useState(
    initial.shelf_life_days != null ? String(initial.shelf_life_days) : ''
  );
  const [storage, setStorage] = useState(initial.storage_type ?? 'dry');
  const [barcode, setBarcode] = useState(initial.barcode ?? '');

  const units = useQuery({
    queryKey: ['menu', 'units'],
    queryFn: () => api.get('/menu/units'),
    staleTime: 60 * 60_000,
  });

  const categories = useQuery({
    queryKey: ['menu', 'ingredient-categories'],
    queryFn: () => api.get('/menu/ingredients/categories'),
    staleTime: 60 * 60_000,
  });

  /**
   * The code is saved on its own endpoint, because it has its own rule: it
   * must not already be on something else, and that clash needs naming
   * rather than folding into a general "could not save".
   */
  const saveBarcode = useMutation({
    mutationFn: (id) =>
      api.put(`/menu/ingredients/${id}/barcode`, { barcode: barcode.trim() || null }),
  });

  const save = useMutation({
    mutationFn: () => {
      const body = {
        name: name.trim(),
        category: category.trim() || null,
        unitId,
        reorderLevel: reorderLevel === '' ? 0 : Number(reorderLevel),
        maxStockLevel: maxStock === '' ? null : Number(maxStock),
        isPerishable: perishable,
        shelfLifeDays: shelfLife === '' ? null : Number(shelfLife),
        storageType: storage,
      };

      return isNew
        ? api.post('/menu/ingredients', body)
        : api.put(`/menu/ingredients/${initial.id}`, body);
    },
    onSuccess: async (result) => {
      const id = initial.id ?? result?.id ?? result?.ingredient?.id;
      const before = initial.barcode ?? '';

      if (id && barcode.trim() !== before) await saveBarcode.mutateAsync(id);
      onSaved();
    },
  });

  const shelfLifeIsSilly = shelfLife !== '' && !(Number(shelfLife) > 0);
  const ceilingBelowLevel =
    maxStock !== '' && reorderLevel !== '' && Number(maxStock) < Number(reorderLevel);

  const ready = name.trim() && unitId && !shelfLifeIsSilly && !ceilingBelowLevel;

  const field =
    'h-11 w-full rounded-lg border border-rule bg-surface-3 px-3 text-sm text-ink focus:border-brand-400 focus:outline-none';

  return (
    <div className="mb-5 rounded-xl border border-rule bg-surface p-5">
      <h3 className="mb-3 font-medium text-ink">
        {isNew ? 'New ingredient' : name || 'Change ingredient'}
      </h3>

      <div className="grid gap-3 sm:grid-cols-3">
        <label className="sm:col-span-2">
          <span className="mb-1.5 block text-xs text-ink-3">Name</span>
          <input value={name} onChange={(e) => setName(e.target.value)} className={field} />
        </label>

        <label>
          <span className="mb-1.5 block text-xs text-ink-3">Measured in</span>
          <select
            value={unitId}
            onChange={(e) => setUnitId(e.target.value)}
            aria-label="Measured in"
            className={field}
          >
            <option value="">Choose a unit</option>
            {(units.data?.units ?? []).map((u) => (
              <option key={u.id} value={u.id}>
                {u.name} ({u.abbreviation})
              </option>
            ))}
          </select>
        </label>

        <label>
          <span className="mb-1.5 block text-xs text-ink-3">Category</span>
          <input
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            list="ingredient-categories"
            aria-label="Category"
            className={field}
          />
          {/* Offered rather than imposed: a new category is sometimes right,
              but "Dairy" and "dairy " as two categories never is. */}
          <datalist id="ingredient-categories">
            {(categories.data?.categories ?? []).map((c) => (
              <option key={c} value={c} />
            ))}
          </datalist>
        </label>

        <label>
          <span className="mb-1.5 block text-xs text-ink-3">
            Barcode <span className="text-ink-3">— scan it in</span>
          </span>
          <input
            value={barcode}
            onChange={(e) => setBarcode(e.target.value)}
            aria-label="Barcode"
            className={`${field} font-mono`}
          />
        </label>

        <label>
          <span className="mb-1.5 block text-xs text-ink-3">Order more below</span>
          <input
            type="number"
            min="0"
            step="0.001"
            value={reorderLevel}
            onChange={(e) => setReorderLevel(e.target.value)}
            aria-label="Order more below"
            className={`${field} text-right tabular-nums`}
          />
        </label>

        <label>
          <span className="mb-1.5 block text-xs text-ink-3">Keep at most</span>
          <input
            type="number"
            min="0"
            step="0.001"
            value={maxStock}
            onChange={(e) => setMaxStock(e.target.value)}
            aria-label="Keep at most"
            className={`${field} text-right tabular-nums`}
          />
        </label>
      </div>

      {ceilingBelowLevel && (
        <p className="mt-2 text-sm text-warn">
          The ceiling is below the reorder level, so this would be ordered and then immediately be
          over its limit.
        </p>
      )}

      <div className="mt-4 rounded-lg border border-rule p-3">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={perishable}
            onChange={(e) => setPerishable(e.target.checked)}
            className="h-5 w-5 rounded border-rule accent-brand-400"
          />
          <span className="text-sm text-ink">This goes off</span>
        </label>

        {perishable && (
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <label>
              <span className="mb-1.5 block text-xs text-ink-3">Lasts how many days</span>
              <input
                type="number"
                min="1"
                step="1"
                value={shelfLife}
                onChange={(e) => setShelfLife(e.target.value)}
                aria-label="Lasts how many days"
                className={`${field} text-right tabular-nums`}
              />
            </label>

            <label>
              <span className="mb-1.5 block text-xs text-ink-3">Kept in</span>
              <select
                value={storage}
                onChange={(e) => setStorage(e.target.value)}
                aria-label="Kept in"
                className={field}
              >
                {Object.entries(STORAGE).map(([key, label]) => (
                  <option key={key} value={key}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
          </div>
        )}

        {shelfLifeIsSilly && (
          <p className="mt-2 text-sm text-warn">A shelf life has to be at least a day.</p>
        )}

        {/*
          Said rather than refused. Sometimes nobody knows yet, and blocking
          the whole ingredient over it helps nothing — but the consequence is
          worth stating, because it is not obvious.
        */}
        {perishable && shelfLife === '' && (
          <p className="mt-2 text-sm text-ink-3">
            With no shelf life, nothing can be suggested when this is delivered — the date has to be
            read off the packet every time, and anything taken in without one will never appear on
            the expiring report.
          </p>
        )}
      </div>

      {(save.isError || saveBarcode.isError) && (
        <p className="mt-3 rounded-lg bg-bad-bg px-3.5 py-2.5 text-sm text-bad">
          {save.error?.message ?? saveBarcode.error?.message ?? 'That did not work.'}
        </p>
      )}

      <div className="mt-4 flex justify-end gap-2">
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button onClick={() => save.mutate()} disabled={!ready || save.isPending}>
          {save.isPending ? 'Saving' : 'Save'}
        </Button>
      </div>
    </div>
  );
}
